import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import host from "../../global";
import Cookies from "js-cookie";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import { ArrowDownToLine, Download } from "lucide-react";
import { getWhiteLabelFromInterview } from "../../utils/whitelabel";
import { formatReportDate } from "../../utils/date";
import { ORG_VIDEO_RESPONSE } from "../geniehireadmin/utils/constants";
// jsPDF's built-in Helvetica can't draw Hindi/Japanese (non-Latin) - those reports render the
const pick = (useEn, native, en) => (useEn && en != null && en !== "" ? en : native);

const PDFGenerator = ({ name, role, scores, stages, interview_id, org_id }) => {
  const [MCQTestResultData, setMCQTestResultData] = useState({});
  const [codingProblems, setProblems] = useState([]);
  const [codeSubmissions, setCodeSubmissions] = useState({});
  const [evaluationData, setEvaluationData] = useState({});
  const [typingTestData, setTypingTestData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioIndex, setAudioIndex] = useState(null);
  const audioRefs = useRef([]);
  const [branding, setBranding] = useState(null);
  // typing_accuracy carries WPM, not a percentage — averaging it in skews the total.
  const scoreEntries = Object.entries(scores || {}).filter(
    ([key]) => key !== "typing_accuracy"
  );
  const averageScore =
    scoreEntries.length > 0
      ? (
          scoreEntries.reduce((acc, [, v]) => acc + parseFloat(v || 0), 0) /
          scoreEntries.length
        ).toFixed(2)
      : "0.00";
  const accessToken = Cookies.get("accessToken");

  // Function to load and add logo to PDF
  const addLogoToPDF = async (pdf, pageWidth, margin, currentY) => {
    return new Promise(async (resolve, reject) => {
      // Use primary logo for PDF header (or fallback to default)
      let logoUrl = branding?.logoPrimary || "/genie-demo-logo-black.png";

      console.log("Loading PDF logo from:", logoUrl);

      let blobUrl = null; 

      try {
        // For S3 URLs, use proxy fetch to avoid CORS issues
        if (logoUrl.startsWith('http')) {
          try {
            const response = await axios.get(`${host}/api/proxy-image`, {
              params: { url: logoUrl },
              responseType: 'blob',
              headers: { Authorization: `Bearer ${accessToken}` }
            });
            const blob = response.data;
            blobUrl = URL.createObjectURL(blob);
            logoUrl = blobUrl;
          } catch (proxyError) {
            console.warn("Proxy fetch failed, trying direct fetch:", proxyError);
            const response = await fetch(logoUrl, { mode: 'no-cors' });
            const blob = await response.blob();
            blobUrl = URL.createObjectURL(blob);
            logoUrl = blobUrl;
          }
        }

        const img = new Image();

        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            const imgData = canvas.toDataURL("image/png");

            const logoWidth = 35;
            const logoHeight = (img.height / img.width) * logoWidth;
            const logoX = pageWidth - margin - logoWidth;
            const logoY = margin;

            pdf.addImage(imgData, "PNG", logoX, logoY, logoWidth, logoHeight);

            console.log("Logo added to PDF successfully");

            resolve();
          } catch (error) {
            console.error("Failed to add logo:", error);
            resolve();
          } finally {
            // Always clean up blob URL
            if (blobUrl) {
              URL.revokeObjectURL(blobUrl);
            }
          }
        };

        img.onerror = (error) => {
          console.error("Failed to load logo image from:", logoUrl, error);

          // Clean up blob URL
          if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
          }

          resolve(); // Continue PDF generation even if logo fails
        };

        img.src = logoUrl;

      } catch (error) {
        console.error("Failed to fetch logo:", error);

        // Clean up blob URL on fetch error
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }

        resolve(); // Continue PDF generation even if logo fails
      }
    });
  };

  const addWatermark = async (pdf, pageWidth, pageHeight) => {
    return new Promise((resolve) => {
      try {
        // Create a canvas to draw the watermark
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas size (higher resolution for better quality)
        canvas.width = pageWidth * 8;
        canvas.height = pageHeight * 8;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Set up text styling
        ctx.font = "bold 160px Arial";
        ctx.fillStyle = "rgba(220, 220, 220, 0.8)";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Save context and rotate
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((-45 * Math.PI) / 180);

        // Draw text - use white-label watermark if available
        const watermarkText = (branding && branding.pdfWatermark) || "genieHire.ai";
        ctx.fillText(watermarkText, 0, 0);

        // Restore context
        ctx.restore();

        // Convert canvas to image data
        const imgData = canvas.toDataURL("image/png");

        // Add as image to PDF (images are not selectable)
        pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);

        resolve();
      } catch (error) {
        console.warn("Failed to create image watermark:", error);
        resolve();
      }
    });
  };

  const handleToggleDetails = (problemId) => {
    setSelectedProblemId(selectedProblemId === problemId ? null : problemId);
  };

  const toggleAudio = (index) => {
    if (audioIndex === index && audioPlaying) {
      audioRefs.current[index].pause();
      setAudioPlaying(false);
      setAudioIndex(null);
    } else {
      audioRefs.current[index].play();
      setAudioPlaying(true);
      setAudioIndex(index);
      audioRefs.current.forEach((audio, i) => {
        if (i !== index) {
          audio.pause();
        }
      });
    }
  };

  const generatePDF = useCallback(async () => {
    if (loading) {
      toast.warn("Data is still loading, cannot generate PDF yet.");
      return;
    }
    // Hindi/Japanese can't render in the PDF font — translate to English on download (cached
    // server-side, so only the first download waits). en holds the merged _en arrays for the loop;
    // useEn is driven by the report's stored language (pdf_use_english), not by inspecting characters.
    const useEn = evaluationData.pdf_use_english === true;
    let en = evaluationData;
    if (useEn && !evaluationData.questions_en) {
      const toastId = toast.loading("Preparing report…");
      try {
        const res = await axios.post(
          `${host}/translate-report/${interview_id}`,
          {},
          { headers: { Authorization: `Bearer ${accessToken}` }, params: { org_id } }
        );
        en = { ...evaluationData, ...res.data };
      } catch (e) {
        toast.warn("Couldn't translate the report; some text may not render.");
      } finally {
        toast.dismiss(toastId);
      }
    }

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - 2 * margin;

      let currentY = margin;
      let pageCount = 1;

      const addNewPage = () => {
        pdf.addPage();
        pageCount++;
        currentY = margin;
        addWatermark(pdf, pageWidth, pageHeight);
        pdf.setFontSize(10);
        pdf.text(
          `Candidate Report - ${name || "Unknown"} (Page ${pageCount})`,
          margin,
          currentY
        );
        currentY += 10;
      };

      await addLogoToPDF(pdf, pageWidth, margin, currentY);
await addWatermark(pdf, pageWidth, pageHeight);

// Header with candidate photo
currentY += 10;
const headerStartY = currentY;

// Add candidate photo if available
let photoAdded = false;
if (evaluationData.candidate_photo_url) {
  try {
    let photoUrl = evaluationData.candidate_photo_url;
    let blobUrl = null;

    // For S3 URLs, use proxy fetch to avoid CORS issues
    if (photoUrl.startsWith('http')) {
      try {
        const response = await axios.get(`${host}/api/proxy-image`, {
          params: { url: photoUrl },
          responseType: 'blob',
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const blob = response.data;
        blobUrl = URL.createObjectURL(blob);
        photoUrl = blobUrl;
      } catch (proxyError) {
        console.warn("Proxy fetch failed for candidate photo, trying direct fetch:", proxyError);
        const response = await fetch(photoUrl, { mode: 'no-cors' });
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);
        photoUrl = blobUrl;
      }
    }

    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          const imgData = canvas.toDataURL("image/png");

          // Photo dimensions and position (left side)
          const photoWidth = 35; // 35mm wide
          const photoHeight = 28; // 25mm tall
          const photoX = margin;
          const photoY = currentY-5;

          // Add circular clipping for photo
          pdf.addImage(imgData, "PNG", photoX, photoY, photoWidth, photoHeight);
          
          photoAdded = true;
          resolve();
        } catch (error) {
          console.error("Failed to add candidate photo:", error);
          resolve();
        } finally {
          if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
          }
        }
      };

      img.onerror = (error) => {
        console.error("Failed to load candidate photo:", error);
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
        resolve();
      };

      img.src = photoUrl;
    });
  } catch (error) {
    console.error("Error loading candidate photo:", error);
  }
}

// Text content position (adjusted if photo exists)
const textX = photoAdded ? margin + 40 : margin; // 35mm = 30mm photo + 5mm gap

pdf.setFontSize(16);
pdf.setTextColor(0, 0, 0);
pdf.text(`Candidate Report - ${name || "Unknown"}`, textX, currentY);
currentY += 10;

pdf.setFontSize(10);
pdf.text(`Job Role: ${role || "NA"}`, textX, currentY);
currentY += 5;
pdf.text(`Total Average Percent: ${averageScore}%`, textX, currentY);
currentY += 5;
pdf.text(
  `Date: ${formatReportDate(
    MCQTestResultData.startDateTime || evaluationData.startDateTime
  )}`,
  textX,
  currentY
);

// Adjust currentY to account for photo height if photo was added
if (photoAdded) {
  const photoEndY = headerStartY + 25; // 30mm photo height
  if (currentY < photoEndY) {
    currentY = photoEndY;
  }
}

currentY += 10;

pdf.setFont("Helvetica", "normal");

      // MCQ Section (Render if data exists)
      if (Object.keys(MCQTestResultData).length > 0) {
        if (currentY > pageHeight - 80) addNewPage();
        console.log("Rendering MCQ Section");
        pdf.setFontSize(14);
        pdf.text(`MCQ Test - ${scores.mcq_percent || 0}%`, margin, currentY);
        currentY += 10;

        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 5;

        if (
          MCQTestResultData.mcqs &&
          Object.keys(MCQTestResultData.mcqs).length > 0
        ) {
          Object.values(MCQTestResultData.mcqs).forEach((question, index) => {
            if (currentY > pageHeight - 70) addNewPage();
            pdf.setFontSize(12);
            pdf.setTextColor(0, 0, 100);
            const splitQuestion = pdf.splitTextToSize(
              `Question ${index + 1}: ${question.text || "N/A"}`,
              contentWidth
            );
            if (currentY + splitQuestion.length * 5 > pageHeight - 30)
              addNewPage();
            pdf.text(splitQuestion, margin, currentY);
            currentY += splitQuestion.length * 5 + 2;

            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            // Options are a list of any length; answers resolve by option id.
            const options = question.options || [];
            const correctOption = options.find(
              (o) => o.id === question.correct_option_id
            );
            const givenOption = options.find(
              (o) => o.id === question.selected_option_id
            );
            if (options.length > 0) {
              options.forEach((option, optIndex) => {
                const splitOption = pdf.splitTextToSize(
                  `${String.fromCharCode(65 + optIndex)}. ${option.text}`,
                  contentWidth - 10
                );
                if (currentY + splitOption.length * 5 > pageHeight - 30)
                  addNewPage();
                pdf.text(splitOption, margin + 5, currentY);
                currentY += splitOption.length * 5 + 2;
              });
            } else {
              pdf.text("No options available", margin + 5, currentY);
              currentY += 5;
            }

            if (correctOption) {
              const splitCorrect = pdf.splitTextToSize(
                `Correct Answer: ${correctOption.text}`,
                contentWidth - 10
              );
              if (currentY + splitCorrect.length * 5 > pageHeight - 30)
                addNewPage();
              pdf.text(splitCorrect, margin + 5, currentY);
              currentY += splitCorrect.length * 5 + 2;
            }

            if (givenOption) {
              if (currentY > pageHeight - 30) addNewPage();
              const splitUser = pdf.splitTextToSize(
                `Your Answer: ${givenOption.text}`,
                contentWidth - 10
              );
              pdf.setFont("Helvetica", "bold");
              pdf.setTextColor(
                question.score === 1 ? 0 : 255,
                question.score === 1 ? 128 : 0,
                0
              );
              if (currentY + splitUser.length * 5 > pageHeight - 30)
                addNewPage();
              pdf.text(splitUser, margin + 5, currentY);
              pdf.setTextColor(0, 0, 0);
              pdf.setFont("Helvetica", "normal");
              currentY += splitUser.length * 5 + 2;
            }

            if (question.score !== undefined && question.score !== null) {
              pdf.text(`Score: ${question.score || 0}`, margin + 5, currentY);
              currentY += 5;
            } else if (!givenOption) {
              if (currentY > pageHeight - 30) addNewPage();
              pdf.setTextColor(255, 0, 0);
              pdf.setFont("Helvetica", "bold");
              pdf.text("Not Attempted", margin + 5, currentY);
              pdf.setTextColor(0, 0, 0);
              pdf.setFont("Helvetica", "normal");
              currentY += 10;
            }

            currentY += 10;
            if (index < Object.values(MCQTestResultData.mcqs).length - 1) {
              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin, currentY - 5, pageWidth - margin, currentY - 5);
            }
          });
        } else {
          pdf.setFontSize(10);
          pdf.text("No MCQ data available", margin, currentY);
          currentY += 10;
        }
      }

      // Coding Section (Render if data exists)
      if (codingProblems.length > 0) {
        if (currentY > pageHeight - 80) addNewPage();
        console.log("Rendering Coding Section");
        pdf.setFontSize(14);
        pdf.text(
          `Coding Test - ${scores.coding_percent || 0}%`,
          margin,
          currentY
        );
        currentY += 10;

        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 5;

        if (codingProblems.length > 0) {
          codingProblems.forEach((problem, index) => {
            const problemId = problem.id;
            const submission = codeSubmissions[problemId] || {};
            const testCases = submission?.test_cases_passed || [];

            if (currentY > pageHeight - 70) addNewPage();
            pdf.setFontSize(12);
            pdf.setTextColor(0, 0, 100);
            pdf.text(
              `Problem ${index + 1}: ${problem.title || "N/A"}`,
              margin,
              currentY
            );
            currentY += 7;

            if (problem.description) {
              const splitDescription = pdf.splitTextToSize(
                `Description: ${problem.description}`,
                contentWidth
              );
              if (currentY + splitDescription.length * 5 > pageHeight - 30)
                addNewPage();
              pdf.setFontSize(10);
              pdf.setTextColor(0, 0, 0);
              pdf.text(splitDescription, margin, currentY);
              currentY += splitDescription.length * 5 + 5;
            }

            if (problem.input_format) {
              const splitInputFormat = pdf.splitTextToSize(
                `Input Format: ${problem.input_format}`,
                contentWidth
              );
              if (currentY + splitInputFormat.length * 5 > pageHeight - 30)
                addNewPage();
              pdf.text(splitInputFormat, margin, currentY);
              currentY += splitInputFormat.length * 5 + 5;
            }

            if (problem.output_format) {
              const splitOutputFormat = pdf.splitTextToSize(
                `Output Format: ${problem.output_format}`,
                contentWidth
              );
              if (currentY + splitOutputFormat.length * 5 > pageHeight - 30)
                addNewPage();
              pdf.text(splitOutputFormat, margin, currentY);
              currentY += splitOutputFormat.length * 5 + 5;
            }

            if (problem.constraints) {
              const splitConstraints = pdf.splitTextToSize(
                `Constraints: ${problem.constraints}`,
                contentWidth
              );
              if (currentY + splitConstraints.length * 5 > pageHeight - 30)
                addNewPage();
              pdf.text(splitConstraints, margin, currentY);
              currentY += splitConstraints.length * 5 + 5;
            }

            if (problem.examples?.length > 0) {
              pdf.text("Examples:", margin, currentY);
              currentY += 5;
              problem.examples.forEach((example, exIndex) => {
                if (currentY > pageHeight - 50) addNewPage();
                const splitInput = pdf.splitTextToSize(
                  `Input ${exIndex + 1}: ${example.input || "N/A"}`,
                  contentWidth - 10
                );
                const splitOutput = pdf.splitTextToSize(
                  `Output ${exIndex + 1}: ${example.output || "N/A"}`,
                  contentWidth - 10
                );
                const splitExplanation = example.explanation
                  ? pdf.splitTextToSize(
                      `Explanation ${exIndex + 1}: ${example.explanation}`,
                      contentWidth - 10
                    )
                  : [];
                if (
                  currentY +
                    (splitInput.length +
                      splitOutput.length +
                      splitExplanation.length) *
                      5 >
                  pageHeight - 30
                )
                  addNewPage();
                pdf.text(splitInput, margin + 5, currentY);
                currentY += splitInput.length * 5 + 2;
                pdf.text(splitOutput, margin + 5, currentY);
                currentY += splitOutput.length * 5 + 2;
                if (example.explanation) {
                  pdf.text(splitExplanation, margin + 5, currentY);
                  currentY += splitExplanation.length * 5 + 2;
                }
              });
              currentY += 5;
            }

            if (submission.language && submission.code) {
              if (currentY > pageHeight - 70) addNewPage();
              pdf.text("Submission Details:", margin, currentY);
              currentY += 5;
              pdf.text(
                `Language: ${submission.language}`,
                margin + 5,
                currentY
              );
              currentY += 5;
              const splitCode = pdf.splitTextToSize(
                `Code: ${submission.code}`,
                contentWidth - 10
              );
              if (currentY + splitCode.length * 5 > pageHeight - 30)
                addNewPage();
              pdf.text(splitCode, margin + 5, currentY);
              currentY += splitCode.length * 5 + 5;
              const acceptedTestCasesCount = Array.isArray(testCases)
                ? testCases.filter((tc) => tc.status_message === "Accepted")
                    .length
                : 0;
              const totalTestCasesCount = Array.isArray(testCases)
                ? testCases.length
                : 0;
              if (problem.track === "apex") {
                // Apex answers are AI-reviewed against a rubric; there are no test cases.
                const reviewStatus = submission.evaluation_status || "pending";
                pdf.text(
                  reviewStatus === "completed"
                    ? `AI Review Score: ${Math.round(submission.coding_score ?? 0)}/100`
                    : reviewStatus === "failed"
                    ? "AI Review: could not be completed"
                    : "AI Review: still in progress",
                  margin + 5,
                  currentY
                );
                currentY += 6;
                if (reviewStatus === "completed") {
                  if (submission.overall_remark) {
                    const splitRemark = pdf.splitTextToSize(
                      `Summary: ${submission.overall_remark}`,
                      contentWidth - 10
                    );
                    if (currentY + splitRemark.length * 5 > pageHeight - 30)
                      addNewPage();
                    pdf.text(splitRemark, margin + 5, currentY);
                    currentY += splitRemark.length * 5 + 3;
                  }
                  (submission.rubric_scores || []).forEach((criterion) => {
                    const splitCriterion = pdf.splitTextToSize(
                      `${criterion.name} (weight ${criterion.weight}%): ${Math.round(criterion.score)}/100` +
                        (criterion.justification ? ` - ${criterion.justification}` : ""),
                      contentWidth - 10
                    );
                    if (currentY + splitCriterion.length * 5 > pageHeight - 30)
                      addNewPage();
                    pdf.text(splitCriterion, margin + 5, currentY);
                    currentY += splitCriterion.length * 5 + 2;
                  });
                }
              } else {
                pdf.text(
                  `Test Cases Passed: ${acceptedTestCasesCount} / ${totalTestCasesCount}`,
                  margin + 5,
                  currentY
                );
                currentY += 5;
              }

              if (selectedProblemId === problemId && Array.isArray(testCases)) {
                if (currentY > pageHeight - 70) addNewPage();
                pdf.text("Test Case Details:", margin, currentY);
                currentY += 5;
                testCases.forEach((testCase, tcIndex) => {
                  if (currentY > pageHeight - 50) addNewPage();
                  pdf.text(`Test Case ${tcIndex + 1}:`, margin + 5, currentY);
                  currentY += 5;
                  pdf.text(
                    `Status: ${testCase.status_message || "N/A"}`,
                    margin + 10,
                    currentY
                  );
                  currentY += 5;
                  const splitYourOutput = pdf.splitTextToSize(
                    `Your Output: ${testCase.yourOutput || "N/A"}`,
                    contentWidth - 20
                  );
                  if (currentY + splitYourOutput.length * 5 > pageHeight - 30)
                    addNewPage();
                  pdf.text(splitYourOutput, margin + 10, currentY);
                  currentY += splitYourOutput.length * 5 + 2;
                  const splitExpectedOutput = pdf.splitTextToSize(
                    `Expected Output: ${testCase.expectedOutput || "N/A"}`,
                    contentWidth - 20
                  );
                  if (
                    currentY + splitExpectedOutput.length * 5 >
                    pageHeight - 30
                  )
                    addNewPage();
                  pdf.text(splitExpectedOutput, margin + 10, currentY);
                  currentY += splitExpectedOutput.length * 5 + 2;
                  if (testCase.stderr) {
                    const splitStderr = pdf.splitTextToSize(
                      `Stderr: ${testCase.stderr}`,
                      contentWidth - 20
                    );
                    if (currentY + splitStderr.length * 5 > pageHeight - 30)
                      addNewPage();
                    pdf.text(splitStderr, margin + 10, currentY);
                    currentY += splitStderr.length * 5 + 2;
                  }
                  currentY += 5;
                });
              }
            } else {
              if (currentY > pageHeight - 30) addNewPage();
              pdf.setTextColor(255, 0, 0);
              pdf.setFont("Helvetica", "bold");
              pdf.text("Not Attempted", margin + 5, currentY);
              pdf.setTextColor(0, 0, 0);
              pdf.setFont("Helvetica", "normal");
              currentY += 10;
            }

            currentY += 10;
            if (index < codingProblems.length - 1) {
              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin, currentY - 5, pageWidth - margin, currentY - 5);
            }
          });
        } else {
          pdf.setFontSize(10);
          pdf.text("No coding problems available", margin, currentY);
          currentY += 10;
        }
      }

      // Behavioural Section — a single score line, no per-question breakdown by
      // design (documentation/behavioral-assessment/behavioral-test-phase1-proposal.md
      // §7: "no per-question evidence trail, no visualization beyond a simple list").
      if (scores.behavioral_percent !== undefined && scores.behavioral_percent !== null) {
        if (currentY > pageHeight - 40) addNewPage();
        pdf.setFontSize(14);
        pdf.text(`Behavioural Assessment - ${scores.behavioral_percent || 0}%`, margin, currentY);
        currentY += 10;
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 10;
      }

      if (Object.keys(evaluationData).length > 0) {
        if (currentY > pageHeight - 80) addNewPage();
        console.log("Rendering AI Interview Section");
        
        // AI Interview title on the right
        pdf.setFontSize(14);
        const aiInterviewText = `AI Interview - ${scores.interview_percent || 0}%`;
        const aiInterviewTextWidth = pdf.getTextWidth(aiInterviewText);
        const aiInterviewX = pageWidth - margin - aiInterviewTextWidth;
        pdf.text(aiInterviewText, aiInterviewX, currentY);

        // Added Introduction Video link on the left
        if (evaluationData.introduction_video_url) {
          const introVideoLinkText = "Download Introduction Video";
          const introVideoLinkX = margin;
          const introVideoLinkY = currentY;
          const introVideoLinkWidth = pdf.getTextWidth(introVideoLinkText);
          const introVideoLinkHeight = 5;

          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 255);
          pdf.text(introVideoLinkText, introVideoLinkX, introVideoLinkY);

          pdf.link(
            introVideoLinkX,
            introVideoLinkY - 4,
            introVideoLinkWidth,
            introVideoLinkHeight,
            {
              url: evaluationData.introduction_video_url,
            }
          );

          pdf.setTextColor(0, 0, 0);
        }
      currentY += 5;

      // Separator line below both elements
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 10;

        if (
          evaluationData.questions &&
          Array.isArray(evaluationData.questions) &&
          evaluationData.questions.length > 0
        ) {
          evaluationData.questions.forEach((question, index) => {
            if (currentY > pageHeight - 70) addNewPage();
            pdf.setFontSize(12);
            pdf.setTextColor(0, 0, 100);
            const splitQuestion = pdf.splitTextToSize(
              `Question ${index + 1}: ${pick(useEn, question, en.questions_en?.[index]) || "N/A"}`,
              contentWidth
            );
            if (currentY + splitQuestion.length * 5 > pageHeight - 30)
              addNewPage();
            pdf.text(splitQuestion, margin, currentY);
            currentY += splitQuestion.length * 5 + 2;

            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            const answer = pick(useEn, evaluationData.answers[index], en.answers_en?.[index]) || "NA";
            const splitAnswer = pdf.splitTextToSize(
              `Answer: ${answer}`,
              contentWidth - 10
            );
            if (currentY + splitAnswer.length * 5 > pageHeight - 30)
              addNewPage();
            pdf.text(splitAnswer, margin + 5, currentY);
            currentY += splitAnswer.length * 5 + 2;

            const score =
              evaluationData.scores[index] !== undefined &&
              evaluationData.scores[index] !== null
                ? evaluationData.scores[index]
                : "NA";
            pdf.text(`Score: ${score}`, margin + 5, currentY);
            currentY += 5;

            const remark = evaluationData.remarks[index] || "NA";
            const splitRemark = pdf.splitTextToSize(
              `Remark: ${remark}`,
              contentWidth - 10
            );
            if (currentY + splitRemark.length * 5 > pageHeight - 30)
              addNewPage();
            pdf.text(splitRemark, margin + 5, currentY);
            currentY += splitRemark.length * 5 + 2;

            if (evaluationData.audio_urls && evaluationData.audio_urls[index]) {
              if (currentY > pageHeight - 30) addNewPage();
              const audioLinkText = "Audio Response";
              const audioLinkX = margin + 5;
              const audioLinkY = currentY;
              const audioLinkWidth = pdf.getTextWidth(audioLinkText);
              const audioLinkHeight = 5;

              pdf.setTextColor(0, 0, 255);
              pdf.text(audioLinkText, audioLinkX, audioLinkY);

              pdf.link(
                audioLinkX,
                audioLinkY - 4,
                audioLinkWidth,
                audioLinkHeight,
                {
                  url: evaluationData.audio_urls[index],
                }
              );

              pdf.setTextColor(0, 0, 0);
              currentY += 7;
            }

            // Render video_urls if available, otherwise audio_urls
            if (evaluationData.video_urls?.[index] && ORG_VIDEO_RESPONSE.includes(org_id)) {
              if (currentY > pageHeight - 30) addNewPage();
              const videoLinkText = "Video Response";
              const videoLinkX = margin + 5;
              const videoLinkY = currentY;
              const videoLinkWidth = pdf.getTextWidth(videoLinkText);
              const videoLinkHeight = 5;

              pdf.setTextColor(0, 0, 255);
              pdf.text(videoLinkText, videoLinkX, videoLinkY);

              pdf.link(
                videoLinkX,
                videoLinkY - 4,
                videoLinkWidth,
                videoLinkHeight,
                {
                  url: evaluationData.video_urls[index],
                }
              );
            }

            // Render follow-ups if available
            if (evaluationData.follow_ups && evaluationData.follow_ups[index] && evaluationData.follow_ups[index].length > 0) {
              currentY += 10;
              if (currentY > pageHeight - 30) addNewPage();

              pdf.setFontSize(10);
              pdf.setTextColor(0, 0, 100);
              pdf.text(`Follow-ups (${evaluationData.follow_ups[index].length}):`, margin + 5, currentY);
              currentY += 7;

              evaluationData.follow_ups[index].forEach((fu, fuIndex) => {
                if (currentY > pageHeight - 50) addNewPage();

                // Follow-up question
                pdf.setFontSize(9);
                pdf.setTextColor(0, 0, 100);
                const splitFuQuestion = pdf.splitTextToSize(
                  `  Follow-up ${fuIndex + 1}: ${pick(useEn, fu.question, en.follow_ups_en?.[index]?.[fuIndex]?.question_en) || "N/A"}`,
                  contentWidth - 15
                );
                pdf.text(splitFuQuestion, margin + 10, currentY);
                currentY += splitFuQuestion.length * 4 + 2;

                // Follow-up answer
                pdf.setFontSize(9);
                pdf.setTextColor(0, 0, 0);
                const splitFuAnswer = pdf.splitTextToSize(
                  `  Response: ${pick(useEn, fu.answer, en.follow_ups_en?.[index]?.[fuIndex]?.answer_en) || "No response"}`,
                  contentWidth - 15
                );
                if (currentY + splitFuAnswer.length * 4 > pageHeight - 30) addNewPage();
                pdf.text(splitFuAnswer, margin + 10, currentY);
                currentY += splitFuAnswer.length * 4 + 2;

                // Follow-up audio link
                if (fu.user_response) {
                  if (currentY > pageHeight - 30) addNewPage();
                  const fuAudioLinkText = "  Audio";
                  const fuAudioLinkX = margin + 10;
                  const fuAudioLinkY = currentY;
                  const fuAudioLinkWidth = pdf.getTextWidth(fuAudioLinkText);
                  const fuAudioLinkHeight = 4;

                  pdf.setTextColor(0, 0, 255);
                  pdf.text(fuAudioLinkText, fuAudioLinkX, fuAudioLinkY);
                  pdf.link(fuAudioLinkX, fuAudioLinkY - 3, fuAudioLinkWidth, fuAudioLinkHeight, {
                    url: fu.user_response,
                  });
                  pdf.setTextColor(0, 0, 0);
                  currentY += 5;
                }

                // Follow-up video link
                if (fu.user_video_response && ORG_VIDEO_RESPONSE.includes(org_id)) {
                  if (currentY > pageHeight - 30) addNewPage();
                  const fuVideoLinkText = "  Video";
                  const fuVideoLinkX = margin + 10;
                  const fuVideoLinkY = currentY;
                  const fuVideoLinkWidth = pdf.getTextWidth(fuVideoLinkText);
                  const fuVideoLinkHeight = 4;

                  pdf.setTextColor(0, 0, 255);
                  pdf.text(fuVideoLinkText, fuVideoLinkX, fuVideoLinkY);
                  pdf.link(fuVideoLinkX, fuVideoLinkY - 3, fuVideoLinkWidth, fuVideoLinkHeight, {
                    url: fu.user_video_response,
                  });
                  pdf.setTextColor(0, 0, 0);
                  currentY += 5;
                }

                currentY += 3;
              });
            }

            currentY += 10;
            if (index < evaluationData.questions.length - 1) {
              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin, currentY - 5, pageWidth - margin, currentY - 5);
            }
          });
        } else {
          pdf.setFontSize(10);
          pdf.text("No AI Interview data available", margin, currentY);
          currentY += 10;
        }
        if (evaluationData.Interview_final_evaluation) {
          if (currentY > pageHeight - 70) addNewPage(); // Only add new page if insufficient space
          pdf.setFontSize(14);
          pdf.text("Smart Evaluation", margin, currentY);
          currentY += 10;

          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, currentY, pageWidth - margin, currentY);
          currentY += 10;

          // Overall Score
          pdf.setFontSize(12);
          pdf.text("Overall Score:", margin, currentY);
          currentY += 7;
          if (
            Object.keys(
              evaluationData.Interview_final_evaluation.overall_score || {}
            ).length > 0
          ) {
            Object.entries(
              evaluationData.Interview_final_evaluation.overall_score
            ).forEach(([category, score]) => {
              pdf.setFontSize(10);
              pdf.text(
                `${
                  category.charAt(0).toUpperCase() + category.slice(1)
                }: ${score}%`,
                margin + 5,
                currentY
              );
              currentY += 5;
            });
            currentY += 5;
          } else {
            pdf.setFontSize(10);
            pdf.text("No overall scores available.", margin + 5, currentY);
            currentY += 10;
          }

          // Skill Performance
          if (currentY > pageHeight - 60) addNewPage();
          pdf.setFontSize(12);
          pdf.text("Skill Performance:", margin, currentY);
          currentY += 7;
          if (
            Object.keys(evaluationData.Interview_skill_performance || {})
              .length > 0
          ) {
            Object.entries(evaluationData.Interview_skill_performance).forEach(
              ([skill, score]) => {
                const skillName = skill.replace(/([A-Z])/g, " $1").trim();
                pdf.setFontSize(10);
                pdf.text(
                  `${skillName}: ${Number(score).toFixed(1)}%`,
                  margin + 5,
                  currentY
                );
                currentY += 5;
              }
            );
            currentY += 5;
          } else {
            pdf.setFontSize(10);
            pdf.text(
              "No skill performance data available.",
              margin + 5,
              currentY
            );
            currentY += 10;
          }

          // Top Skills
          if (currentY > pageHeight - 60) addNewPage();
          pdf.setFontSize(12);
          pdf.text("Top Skills:", margin, currentY);
          currentY += 7;
          if (
            evaluationData.Interview_final_evaluation.top_skills &&
            evaluationData.Interview_final_evaluation.top_skills.length > 0
          ) {
            pdf.setFontSize(10);
            evaluationData.Interview_final_evaluation.top_skills.forEach(
              (skill) => {
                pdf.text(`• ${skill}`, margin + 5, currentY);
                currentY += 5;
              }
            );
            currentY += 5;
          } else {
            pdf.setFontSize(10);
            pdf.text("No top skills identified.", margin + 5, currentY);
            currentY += 10;
          }

          // Improvement Areas
          if (currentY > pageHeight - 60) addNewPage();
          pdf.setFontSize(12);
          pdf.text("Improvement Areas:", margin, currentY);
          currentY += 7;
          if (
            evaluationData.Interview_final_evaluation.improvement_areas &&
            evaluationData.Interview_final_evaluation.improvement_areas.length >
              0
          ) {
            pdf.setFontSize(10);
            evaluationData.Interview_final_evaluation.improvement_areas.forEach(
              (area) => {
                pdf.text(`• ${area}`, margin + 5, currentY);
                currentY += 5;
              }
            );
            currentY += 5;
          } else {
            pdf.setFontSize(10);
            pdf.text("No improvement areas identified.", margin + 5, currentY);
            currentY += 10;
          }

          // Future Potential
          if (currentY > pageHeight - 70) addNewPage();
          pdf.setFontSize(12);
          pdf.text("Future Potential:", margin, currentY);
          currentY += 7;
          pdf.setFontSize(10);
          if (evaluationData.Interview_final_evaluation.future_potential) {
            const potentialText =
              evaluationData.Interview_final_evaluation.future_potential;
            const splitPotential = pdf.splitTextToSize(
              potentialText,
              contentWidth - 10
            );
            if (currentY + splitPotential.length * 5 > pageHeight - 30)
              addNewPage();
            pdf.text(splitPotential, margin + 5, currentY);
            currentY += splitPotential.length * 5 + 5;
          } else {
            pdf.text(
              "No future potential assessment available.",
              margin + 5,
              currentY
            );
            currentY += 10;
          }

          // Recommendation
          if (currentY > pageHeight - 70) addNewPage();
          pdf.setFontSize(12);
          pdf.text("Recommendation:", margin, currentY);
          currentY += 7;
          pdf.setFontSize(10);
          if (evaluationData.Interview_final_evaluation.recommendation) {
            const recommendation =
              evaluationData.Interview_final_evaluation.recommendation.toLowerCase() === "rejected"
                ? "Not Recommended"
                : evaluationData.Interview_final_evaluation.recommendation;
            pdf.text(
              `Decision: ${recommendation}`,
              margin + 5,
              currentY
            );
            currentY += 5;
            if (
              evaluationData.Interview_final_evaluation.recommendation_reason
            ) {
              const reasonText =
                evaluationData.Interview_final_evaluation.recommendation_reason;
              const splitReason = pdf.splitTextToSize(
                `Reason: ${reasonText}`,
                contentWidth - 10
              );
              if (currentY + splitReason.length * 5 > pageHeight - 20)
                addNewPage();
              pdf.text(splitReason, margin + 5, currentY);
              currentY += splitReason.length * 5 + 5;
            }
          } else {
            pdf.text("No recommendation provided.", margin + 5, currentY);
            currentY += 10;
          }

          // Hidden Insights
          if (evaluationData.Interview_final_evaluation.hidden_insights) {
            if (currentY > pageHeight - 70) addNewPage();
            pdf.setFontSize(12);
            pdf.text("Hidden Insights:", margin, currentY);
            currentY += 5;
            const insightsText =
              evaluationData.Interview_final_evaluation.hidden_insights;
            const splitInsights = pdf.splitTextToSize(
              insightsText,
              contentWidth - 10
            );
            if (currentY + splitInsights.length * 5 > pageHeight - 30)
              addNewPage();
            pdf.setFontSize(10);
            pdf.text(splitInsights, margin + 5, currentY);
            currentY += splitInsights.length * 5 + 5;
          }

          // Authenticity Report
          if (
            evaluationData.Interview_final_evaluation
              .authenticity_confidence_score > 0
          ) {
            if (currentY > pageHeight - 70) addNewPage();
            pdf.setFontSize(12);
            pdf.text("Authenticity Report:", margin, currentY);
            currentY += 5;
            pdf.setFontSize(10);
            pdf.text(
              `Authenticity Confidence Score: ${evaluationData.Interview_final_evaluation.authenticity_confidence_score.toFixed(
                1
              )}%`,
              margin + 5,
              currentY
            );
            currentY += 5;
            if (
              evaluationData.Interview_final_evaluation.plagiarism_score > 0
            ) {
              if (currentY > pageHeight - 30) addNewPage();
              pdf.text(
                `Plagiarism Detection Score: ${evaluationData.Interview_final_evaluation.plagiarism_score.toFixed(
                  1
                )}%`,
                margin + 5,
                currentY
              );
              currentY += 5;
            }
            currentY += 10;
          }
        }
      }

      // Typing Test Section (Render if data exists)
      if (
        typingTestData &&
        Object.keys(typingTestData).length > 0 &&
        typingTestData.wpm !== undefined
      ) {
        if (currentY > pageHeight - 80) addNewPage();

        // Extract data - Net WPM is the final score
        const { wpm = 0, accuracy = 0, errors = 0, words_typed = 0, time_taken = '0:00' } = typingTestData;

        // Net WPM is the final score directly
        const netWPMScore = wpm;

        // Get AI-generated performance label
        const performanceText = typingTestData.ai_recommendation?.performance_label || "Not Available";

        pdf.setFontSize(14);
        pdf.text(
          `Typing Test - Net WPM: ${netWPMScore}`,
          margin,
          currentY
        );
        currentY += 10;

        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 8;

        if (currentY > pageHeight - 50) addNewPage();
        pdf.setFontSize(12);
        pdf.text("Performance Metrics:", margin, currentY);
        currentY += 7;

        pdf.setFontSize(10);
        pdf.text(`Net WPM (Correct Words/Min): ${netWPMScore}`, margin + 5, currentY);
        currentY += 6;
        pdf.text(`Accuracy: ${accuracy}%`, margin + 5, currentY);
        currentY += 6;
        pdf.text(`Total Errors: ${errors}`, margin + 5, currentY);
        currentY += 6;
        pdf.text(`Words Typed: ${words_typed}`, margin + 5, currentY);
        currentY += 6;
        pdf.text(`Time Taken: ${time_taken}`, margin + 5, currentY);
        currentY += 10;

        if (currentY > pageHeight - 30) addNewPage();
        pdf.setFontSize(12);
        pdf.text("Performance Level:", margin, currentY);
        currentY += 7;

        pdf.setFontSize(10);
        pdf.setFont("Helvetica", "bold");
        pdf.text(performanceText, margin + 5, currentY);
        pdf.setFont("Helvetica", "normal");
        currentY += 10;

        // Add AI Recommendation if available
        if (typingTestData.ai_recommendation && typingTestData.ai_recommendation.recommendation) {
          if (currentY > pageHeight - 40) addNewPage();
          pdf.setFontSize(12);
          pdf.setFont("Helvetica", "bold");
          pdf.text("AI Recommendation:", margin, currentY);
          currentY += 7;

          pdf.setFont("Helvetica", "normal").setFontSize(10);
          const splitRecommendation = pdf.splitTextToSize(typingTestData.ai_recommendation.recommendation, pageWidth - 2 * margin);
          pdf.text(splitRecommendation, margin + 5, currentY);
          currentY += (splitRecommendation.length * 5) + 10;
        }
      }

      // Smart Evaluation Section (if exists)
      if (
        Object.keys(evaluationData).length > 0 &&
        evaluationData.final_evaluation
      ) {
        addNewPage();
        pdf.setFontSize(14);
        pdf.setTextColor(0, 0, 0);
        pdf.text("Smart Evaluation", margin, currentY);
        currentY += 10;

        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 10;

        // Overall Score
        pdf.setFontSize(12);
        pdf.text("Overall Score:", margin, currentY);
        currentY += 7;

        if (
          Object.keys(evaluationData.final_evaluation.overall_score || {})
            .length > 0
        ) {
          Object.entries(evaluationData.final_evaluation.overall_score).forEach(
            ([category, score]) => {
              pdf.setFontSize(10);
              pdf.text(
                `${
                  category.charAt(0).toUpperCase() + category.slice(1)
                }: ${score}%`,
                margin + 5,
                currentY
              );
              currentY += 5;
            }
          );
          currentY += 5;
        } else {
          pdf.setFontSize(10);
          pdf.text("No overall scores available.", margin + 5, currentY);
          currentY += 10;
        }

        // Skill Performance
        if (currentY > pageHeight - 60) addNewPage();
        pdf.setFontSize(12);
        pdf.text("Skill Performance:", margin, currentY);
        currentY += 7;

        if (Object.keys(evaluationData.skill_performance || {}).length > 0) {
          Object.entries(evaluationData.skill_performance).forEach(
            ([skill, score]) => {
              const skillName = skill.replace(/([A-Z])/g, " $1").trim();
              pdf.setFontSize(10);
              pdf.text(
                `${skillName}: ${Number(score).toFixed(1)}%`,
                margin + 5,
                currentY
              );
              currentY += 5;
            }
          );
          currentY += 5;
        } else {
          pdf.setFontSize(10);
          pdf.text(
            "No skill performance data available.",
            margin + 5,
            currentY
          );
          currentY += 10;
        }

        // Top Skills
        if (currentY > pageHeight - 60) addNewPage();
        pdf.setFontSize(12);
        pdf.text("Top Skills:", margin, currentY);
        currentY += 7;

        if (
          evaluationData.final_evaluation.top_skills &&
          evaluationData.final_evaluation.top_skills.length > 0
        ) {
          pdf.setFontSize(10);
          evaluationData.final_evaluation.top_skills.forEach((skill) => {
            pdf.text(`• ${skill}`, margin + 5, currentY);
            currentY += 5;
          });
          currentY += 5;
        } else {
          pdf.setFontSize(10);
          pdf.text("No top skills identified.", margin + 5, currentY);
          currentY += 10;
        }

        // Improvement Areas
        if (currentY > pageHeight - 60) addNewPage();
        pdf.setFontSize(12);
        pdf.text("Improvement Areas:", margin, currentY);
        currentY += 7;

        if (
          evaluationData.final_evaluation.improvement_areas &&
          evaluationData.final_evaluation.improvement_areas.length > 0
        ) {
          pdf.setFontSize(10);
          evaluationData.final_evaluation.improvement_areas.forEach((area) => {
            pdf.text(`• ${area}`, margin + 5, currentY);
            currentY += 5;
          });
          currentY += 5;
        } else {
          pdf.setFontSize(10);
          pdf.text("No improvement areas identified.", margin + 5, currentY);
          currentY += 10;
        }

        // Future Potential
        if (currentY > pageHeight - 70) addNewPage();
        pdf.setFontSize(12);
        pdf.text("Future Potential:", margin, currentY);
        currentY += 7;

        pdf.setFontSize(10);
        if (evaluationData.final_evaluation.future_potential) {
          const potentialText =
            evaluationData.final_evaluation.future_potential;
          const splitPotential = pdf.splitTextToSize(
            potentialText,
            contentWidth - 10
          );
          if (currentY + splitPotential.length * 5 > pageHeight - 30)
            addNewPage();
          pdf.text(splitPotential, margin + 5, currentY);
          currentY += splitPotential.length * 5 + 5;
        } else {
          pdf.text(
            "No future potential assessment available.",
            margin + 5,
            currentY
          );
          currentY += 10;
        }

        // Recommendation
        if (currentY > pageHeight - 70) addNewPage();
        pdf.setFontSize(12);
        pdf.text("Recommendation:", margin, currentY);
        currentY += 7;

        pdf.setFontSize(10);
        if (evaluationData.final_evaluation.recommendation) {
          pdf.text(
            `Decision: ${evaluationData.final_evaluation.recommendation}`,
            margin + 5,
            currentY
          );
          currentY += 5;
          if (evaluationData.final_evaluation.recommendation_reason) {
            const reasonText =
              evaluationData.final_evaluation.recommendation_reason;
            const splitReason = pdf.splitTextToSize(
              `Reason: ${reasonText}`,
              contentWidth - 10
            );
            if (currentY + splitReason.length * 5 > pageHeight - 20)
              addNewPage();
            pdf.text(splitReason, margin + 5, currentY);
            currentY += splitReason.length * 5 + 5;
          }
        } else {
          pdf.text("No recommendation provided.", margin + 5, currentY);
          currentY += 10;
        }

        // Hidden Insights
        if (evaluationData.final_evaluation.hidden_insights) {
          if (currentY > pageHeight - 70) addNewPage();
          pdf.setFontSize(12);
          pdf.text("Hidden Insights:", margin, currentY);
          currentY += 5;
          const insightsText = evaluationData.final_evaluation.hidden_insights;
          const splitInsights = pdf.splitTextToSize(
            insightsText,
            contentWidth - 10
          );
          if (currentY + splitInsights.length * 5 > pageHeight - 30)
            addNewPage();
          pdf.setFontSize(10);
          pdf.text(splitInsights, margin + 5, currentY);
          currentY += splitInsights.length * 5 + 5;
        }

        // Authenticity Report
        if (evaluationData.final_evaluation.authenticity_confidence_score > 0) {
          if (currentY > pageHeight - 50) addNewPage();
          pdf.setFontSize(12);
          pdf.text("Authenticity Report:", margin, currentY);
          currentY += 5;
          pdf.setFontSize(10);
          pdf.text(
            `Authenticity Confidence Score: ${evaluationData.final_evaluation.authenticity_confidence_score.toFixed(
              1
            )}%`,
            margin + 5,
            currentY
          );
          currentY += 5;
          if (evaluationData.final_evaluation.plagiarism_score > 0) {
            if (currentY > pageHeight - 30) addNewPage();
            pdf.text(
              `Plagiarism Detection Score: ${evaluationData.final_evaluation.plagiarism_score.toFixed(
                1
              )}%`,
              margin + 5,
              currentY
            );
            currentY += 5;
          }
          currentY += 10;
        }
      }

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 25, pageHeight - 10);
      }

      pdf.save(`${name || "Candidate"}_Candidate_Report.pdf`);
    } catch (error) {
      console.error("PDF generation error:", error);
    }
  }, [
    name,
    role,
    averageScore,
    scores,
    stages,
    MCQTestResultData,
    codingProblems,
    codeSubmissions,
    evaluationData,
    typingTestData,
    selectedProblemId,
    accessToken,
    interview_id,
    loading,
    branding,
  ]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);

      // Fetch white-label branding first
      try {
        const brandingConfig = await getWhiteLabelFromInterview(interview_id);
        setBranding(brandingConfig);
      } catch (error) {
        console.error("Failed to load white-label branding:", error);
        setBranding(null);
      }

      try {
        console.log("Fetching MCQ data for interview_id:", interview_id);
        const mcqResponse = await axios.get(`${host}/get_mcqs_test_result`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { interview_id },
        });
        setMCQTestResultData(mcqResponse.data || {});
        console.log("MCQ Data fetched:", mcqResponse.data);
      } catch (error) {
        console.warn(
          "No MCQ data available for interview_id:",
          interview_id,
          error
        );
        setMCQTestResultData({}); // Set empty object if no data
      }

      // Fetch Coding data
      try {
        console.log("Fetching Coding data for interview_id:", interview_id);
        const problemsResponse = await axios.get(
          `${host}/api/problems_from_codesubmissions`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { interview_id },
          }
        );
        setProblems(problemsResponse.data || []);
        console.log("Coding Problems fetched:", problemsResponse.data);

        const submissionsResponse = await axios.get(
          `${host}/api/get_codesubmissions`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { interview_id },
          }
        );
        setCodeSubmissions(submissionsResponse.data.submissions || {});
        console.log(
          "Code Submissions fetched:",
          submissionsResponse.data.submissions
        );
      } catch (error) {
        console.warn(
          "No Coding data available for interview_id:",
          interview_id,
          error
        );
        setProblems([]);
        setCodeSubmissions({});
      }

      // Fetch AI Interview data
      try {
        console.log(
          "Fetching AI Interview data for interview_id:",
          interview_id
        );
        const evalResponse = await axios.get(`${host}/evaluation-v2`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { interview_id, org_id },
        });
        setEvaluationData(evalResponse.data || {});
        console.log("Evaluation Data fetched:", evalResponse.data);
      } catch (error) {
        console.warn(
          "No AI Interview data available for interview_id:",
          interview_id,
          error
        );
        setEvaluationData({});
      }

      // Fetch Typing Test data
      try {
        const typingResponse = await axios.get(
          `${host}/get_typing_test_result`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { interview_id },
          }
        );
        setTypingTestData(typingResponse.data || {});
      } catch (error) {
        // Typing test is optional, silently handle if not available
        setTypingTestData({});
      }

      setLoading(false);
    };

    fetchAllData();
  }, [interview_id, accessToken]);

  return (
    <button title="Download Report" className="dark:text-gray-200 text-textSecondary" onClick={generatePDF} disabled={loading}>
      <ArrowDownToLine className="h-6 w-6" />
    </button>
  );
};

export default PDFGenerator;


